/**
 * Cohort Dashboard — Figma frame 174:2
 *
 *   • Masthead:   cohort switcher + certificate filter pills
 *   • KPI strip:  Enrollment / Active (7-day) / At risk / Avg mastery
 *   • Roster:     searchable, sortable table of enrolled students
 *   • Rail:       AtRiskWidget (compact) with a "See all" link
 *
 * Data comes from the existing hooks:
 *   - useInstructorCohorts()      → cohort list + counts
 *   - useCohortStudents(id)       → roster for the active cohort (falls back
 *                                    to useStudents() when no cohort is picked)
 *   - useDashboardStats()         → aggregate KPI baselines
 *   - useAtRisk() (via widget)    → at-risk rail
 *
 * The mock fallbacks in the API services keep this working in dev without
 * a live backend — that path is exercised by CI + the placement-quiz demo.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  useInstructorCohorts,
  useCohortStudents,
  useStudents,
  useDashboardStats,
} from '../hooks';
import { useAtRisk } from '../hooks/useAtRisk';
import AtRiskWidget from '../components/AtRiskWidget';
import styles from './Cohorts.module.css';

type SortKey = 'name' | 'progress' | 'lastActive';
type CertFilter = 'all' | 'engineering' | 'marketing';

function initialsFrom(first: string, last: string): string {
  return `${(first[0] || '').toUpperCase()}${(last[0] || '').toUpperCase()}` || '??';
}

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true })
      .replace('minutes', 'min')
      .replace('minute', 'min')
      .replace('hours', 'h')
      .replace('hour', 'h')
      .replace('days', 'd')
      .replace('day', 'd')
      .replace('weeks', 'w')
      .replace('week', 'w');
  } catch {
    return iso;
  }
}

const CERT_FILTERS: Array<{ key: CertFilter; label: string }> = [
  { key: 'all', label: 'All certs' },
  { key: 'engineering', label: 'AI Engineering' },
  { key: 'marketing', label: 'AI Marketing' },
];

const Cohorts: React.FC = () => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [certFilter, setCertFilter] = useState<CertFilter>('all');
  const [activeCohortId, setActiveCohortId] = useState<string | null>(null);

  const { data: cohortResp } = useInstructorCohorts();
  const { data: dashStats } = useDashboardStats();
  const { data: atRiskList } = useAtRisk();

  const cohorts = cohortResp?.data ?? [];
  const activeCohort = activeCohortId
    ? cohorts.find((c) => c.id === activeCohortId)
    : cohorts[0];
  const effectiveCohortId = activeCohort?.id ?? '';

  // Prefer per-cohort roster; fall back to the platform-wide student list so
  // the page still renders when no cohort has students yet.
  const { data: cohortStudents } = useCohortStudents(effectiveCohortId);
  const { data: studentsResp, isLoading, isError, refetch } = useStudents({
    limit: 100,
  });

  const rosterSource = useMemo(() => {
    if (cohortStudents && cohortStudents.length > 0) return cohortStudents;
    return studentsResp?.data ?? [];
  }, [cohortStudents, studentsResp]);

  // Normalize to a common row shape so the table doesn't care where the
  // records came from.
  const roster = useMemo(() => {
    const rows = rosterSource.map((s: any) => ({
      id: s.id ?? s.userId,
      firstName: s.firstName ?? (s.userName ?? '').split(' ')[0] ?? '',
      lastName:
        s.lastName ??
        (s.userName ?? '').split(' ').slice(1).join(' ') ??
        '',
      email: s.email ?? s.userEmail ?? '',
      progress:
        typeof s.progress === 'number'
          ? s.progress
          : typeof s.averageScore === 'number'
            ? s.averageScore
            : Math.round((s.labsCompleted ?? 0) * 5),
      lastActive: s.lastActiveAt ?? s.lastActive ?? new Date().toISOString(),
      status: s.status ?? 'active',
      // Approximate cert assignment when the record has no cohort — use a
      // deterministic hash so the same student always lands in the same track.
      cert:
        s.cohortId?.includes('marketing') || (s.id?.charCodeAt(0) ?? 0) % 2 === 0
          ? 'AI Engineering'
          : 'AI Marketing',
    }));

    let filtered = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q),
      );
    }
    if (certFilter !== 'all') {
      const wanted = certFilter === 'engineering' ? 'AI Engineering' : 'AI Marketing';
      filtered = filtered.filter((r) => r.cert === wanted);
    }

    const sorted = [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'name') {
        return dir * `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      }
      if (sortKey === 'progress') {
        return dir * (a.progress - b.progress);
      }
      // lastActive
      return dir * (new Date(a.lastActive).getTime() - new Date(b.lastActive).getTime());
    });

    return sorted;
  }, [rosterSource, search, certFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const kpi = useMemo(() => {
    const enrollment = activeCohort?.studentCount ?? dashStats?.totalStudents ?? roster.length;
    const activeSeven = dashStats?.activeStudents ?? 0;
    const engagement =
      enrollment > 0 ? Math.round((activeSeven / enrollment) * 100) : 0;
    const atRiskCount = atRiskList?.length ?? 0;
    const highRisk = atRiskList?.filter((s) => s.aggregateSeverity === 'high').length ?? 0;
    const avgMastery = dashStats?.averageCompletionRate ?? 0;

    return {
      enrollment,
      newThisMonth: dashStats?.newStudentsThisWeek ? dashStats.newStudentsThisWeek * 4 : 0,
      activeSeven,
      engagement,
      atRiskCount,
      highRisk,
      avgMastery,
    };
  }, [activeCohort, dashStats, atRiskList, roster.length]);

  return (
    <div className={styles.page}>
      {/* ------------------------- Masthead ------------------------- */}
      <header className={styles.masthead}>
        <div className={styles.mastheadCopy}>
          <span className={styles.eyebrow}>Cohort dashboard</span>
          <h1 className={styles.title}>
            {cohorts.length > 0 ? (
              <select
                aria-label="Select cohort"
                className={styles.cohortSelect}
                value={effectiveCohortId}
                onChange={(e) => setActiveCohortId(e.target.value)}
              >
                {cohorts.map((c) => (
                  <option key={c.id} value={c.id}>
                    Cohort · {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <span>Cohort · Autumn 2026</span>
            )}
          </h1>
        </div>

        <div className={styles.certPills} role="group" aria-label="Filter by certificate">
          {CERT_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setCertFilter(f.key)}
              className={`${styles.pill} ${
                certFilter === f.key ? styles.pillActive : ''
              }`}
              aria-pressed={certFilter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      {/* ------------------------- KPI strip ------------------------- */}
      <section className={styles.kpiStrip} aria-label="Cohort metrics">
        <div className={styles.kpiCell}>
          <span className={styles.kpiLabel}>Enrollment</span>
          <span className={styles.kpiValue}>{kpi.enrollment}</span>
          <span className={styles.kpiDelta}>
            {kpi.newThisMonth > 0 ? `+${kpi.newThisMonth} this month` : 'Steady'}
          </span>
        </div>
        <div className={styles.kpiCell}>
          <span className={styles.kpiLabel}>Active (7-day)</span>
          <span className={styles.kpiValue}>{kpi.activeSeven}</span>
          <span className={styles.kpiDelta}>{kpi.engagement}% engagement</span>
        </div>
        <div className={styles.kpiCell}>
          <span className={styles.kpiLabel}>At risk</span>
          <span className={styles.kpiValue}>{kpi.atRiskCount}</span>
          <span
            className={`${styles.kpiDelta} ${
              kpi.highRisk > 0 ? styles.kpiDeltaWarn : ''
            }`}
          >
            {kpi.highRisk > 0
              ? `${kpi.highRisk} high priority`
              : 'No high-severity flags'}
          </span>
        </div>
        <div className={styles.kpiCell}>
          <span className={styles.kpiLabel}>Avg mastery</span>
          <span className={styles.kpiValue}>{kpi.avgMastery}%</span>
          <span className={styles.kpiDelta}>Cohort baseline</span>
        </div>
      </section>

      {/* ------------------------- Body ------------------------- */}
      <div className={styles.body}>
        {/* Roster */}
        <section className={styles.card} aria-label="Roster">
          <header className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Roster</h2>
            <span className={styles.cardMeta}>
              {roster.length} {roster.length === 1 ? 'student' : 'students'}
            </span>
            <div className={styles.cardHeadSpacer} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search students…"
              className={styles.search}
              aria-label="Search roster"
            />
          </header>

          {isLoading && roster.length === 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Student</th>
                  <th>Cert</th>
                  <th>Progress</th>
                  <th>Last active</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className={styles.rowSkeleton}>
                    <td colSpan={5}>
                      <div className={styles.shimmer} style={{ width: '60%' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : isError ? (
            <div className={styles.errorState}>
              <p>Couldn&apos;t load the roster.</p>
              <button
                type="button"
                className={styles.retryBtn}
                onClick={() => refetch()}
              >
                Retry
              </button>
            </div>
          ) : roster.length === 0 ? (
            <div className={styles.empty}>
              <strong>No students yet.</strong>
              Enrol your first cohort to see the roster here.
              <div>
                <Link
                  to="/cohorts"
                  className={styles.emptyAction}
                  onClick={(e) => e.preventDefault()}
                >
                  Enrol students
                </Link>
              </div>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th
                    className={styles.sortable}
                    style={{ width: '40%' }}
                    onClick={() => toggleSort('name')}
                  >
                    Student
                    <span className={styles.sortArrow}>
                      {sortKey === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                  <th>Cert</th>
                  <th
                    className={styles.sortable}
                    onClick={() => toggleSort('progress')}
                  >
                    Progress
                    <span className={styles.sortArrow}>
                      {sortKey === 'progress' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                  <th
                    className={styles.sortable}
                    onClick={() => toggleSort('lastActive')}
                  >
                    Last active
                    <span className={styles.sortArrow}>
                      {sortKey === 'lastActive'
                        ? (sortDir === 'asc' ? '↑' : '↓')
                        : '↕'}
                    </span>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((row) => {
                  const atRisk = row.progress < 50 || row.status === 'offline';
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className={styles.studentCell}>
                          <span className={styles.initials} aria-hidden="true">
                            {initialsFrom(row.firstName, row.lastName)}
                          </span>
                          <div className={styles.studentInfo}>
                            <Link
                              to={`/students/${row.id}`}
                              className={styles.studentName}
                            >
                              {row.firstName} {row.lastName}
                            </Link>
                            <span className={styles.studentEmail}>{row.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className={styles.cellRegular}>{row.cert}</td>
                      <td>
                        <div className={styles.progressCell}>
                          <div className={styles.progressTrack}>
                            <div
                              className={`${styles.progressFill} ${
                                atRisk ? styles.progressFillWarn : ''
                              }`}
                              style={{
                                width: `${Math.max(0, Math.min(100, row.progress))}%`,
                              }}
                            />
                          </div>
                          <span className={styles.cellNumeric}>{row.progress}%</span>
                        </div>
                      </td>
                      <td className={styles.cellMuted}>{relativeTime(row.lastActive)}</td>
                      <td>
                        <span
                          className={`${styles.statusChip} ${
                            atRisk ? styles.statusAtRisk : styles.statusOnTrack
                          }`}
                        >
                          {atRisk ? 'At risk' : 'On track'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {/* At-risk rail — reuse the standalone widget */}
        <aside>
          <AtRiskWidget compact />
        </aside>
      </div>
    </div>
  );
};

export default Cohorts;
